import React, { useEffect, useState, createContext } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import GlobalHeader from '@components/_Global/GlobalHeader/GlobalHeader'
import GlobalNavigation from '@components/_Global/GlobalNavigation/GlobalNavigation'
const Chatbot = React.lazy(() => import('@components/Chatbot/Chatbot'))
const GlobalFooter = React.lazy(
  () => import('@components/_Global/GlobalFooter/GlobalFooter'),
)
import SideBar from '@components/_Global/SideBar/SideBar'
import '@scss/index.scss'
import {
  Breadcrumbs,
  UserType,
  PageType,
  LayoutBasePage,
  LayoutGuidePage,
  LayoutClientNoSideBarPage,
  LayoutIndexPage,
} from '@type/component-data-types.d'
import PageBreadcrumb from '@components/PageBreadcrumb/PageBreadcrumb'
import ScrollToPageTopBtn from '@components/_Global/ScrollToPageTopBtn/ScrollToPageTopBtn'

type LayoutProps = {
  breadcrumb?: Breadcrumbs
  pageType?: PageType
  isHeaderType: UserType
  isFooterType: UserType
  isSideBarType: UserType
  hideChatbot?: boolean
  children: React.ReactNode
}

function BaseComponent(prop: LayoutBasePage): React.JSX.Element {
  return (
    <Container fluid="lg">
      {prop.breadcrumb ? <PageBreadcrumb breadcrumbs={prop.breadcrumb} /> : ''}
      <Row className="d-md-flex d-block justify-content-between">
        <Col className="main">
          <main>{prop.children}</main>
        </Col>
        <Col className="sideBar">
          <SideBar userType={prop.isSideBarType} />
        </Col>
      </Row>
    </Container>
  )
}

function IndexComponent(prop: LayoutIndexPage): React.JSX.Element {
  return <main>{prop.children}</main>
}

function GuideComponentType(prop: LayoutGuidePage): React.JSX.Element {
  return (
    <Container fluid="lg">
      {prop.breadcrumb && <PageBreadcrumb breadcrumbs={prop.breadcrumb} />}
      <Row className="d-md-flex d-block justify-content-between">
        <Col className="main isNoSideBar">
          <main>{prop.children}</main>
        </Col>
      </Row>
    </Container>
  )
}

function ClientNoSideBarComponent(
  prop: LayoutClientNoSideBarPage,
): React.JSX.Element {
  return (
    <Container fluid="lg">
      {prop.breadcrumb && <PageBreadcrumb breadcrumbs={prop.breadcrumb} />}
      <Row className="d-md-flex d-block justify-content-between">
        <Col className="main isNoSideBar">
          <main>{prop.children}</main>
        </Col>
      </Row>
    </Container>
  )
}
export const LayoutScrollContext = createContext({
  isJumpLinksSticky: false,
  registerJumpLinksSentinel: (el: HTMLDivElement | null) => {},
})

const Layout = (prop: LayoutProps): React.JSX.Element => {
  const isIndex = prop.pageType === 'index'
  const isGuide = prop.pageType === 'guide'
  const isClientNoSideBar = prop.pageType === 'client-no-side-bar'
  const [isDisplayBackToTopButton, setIsBackToTopDisplay] = useState(false)
  const [jumpLinksSentinel, setJumpLinksSentinel] =
    useState<HTMLDivElement | null>(null)
  const registerJumpLinksSentinel = (el: HTMLDivElement | null) => {
    setJumpLinksSentinel(el)
  }

  const [jumpLinksFullyOutOfView, setIsTOCFullyAbove] = useState(false)

  useEffect(() => {
    if (!jumpLinksSentinel) return

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting && entry.boundingClientRect.top < 0) {
            setIsTOCFullyAbove(true)
          } else {
            setIsTOCFullyAbove(false)
          }
        })
      },
      { root: null, threshold: 0 },
    )

    observer.observe(jumpLinksSentinel)
    return () => {
      observer.unobserve(jumpLinksSentinel)
    }
  }, [jumpLinksSentinel])

  const [isNearPageBottom, setIsNearPageBottom] = useState(false)
  useEffect(() => {
    const footer = document.getElementById('footer')
    if (!footer) return

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          setIsNearPageBottom(entry.isIntersecting)
        })
      },
      { root: null, threshold: 0 },
    )

    observer.observe(footer)
    return () => {
      if (footer) observer.unobserve(footer)
    }
  }, [])

  const [isJumpLinksSticky, setIsJumpLinksSticky] = useState(false)
  useEffect(() => {
    if (jumpLinksFullyOutOfView && !isNearPageBottom) {
      setIsJumpLinksSticky(true)
    } else {
      setIsJumpLinksSticky(false)
    }
  }, [jumpLinksFullyOutOfView, isNearPageBottom])

  useEffect(() => {
    let boundingClientRectY: number = 0
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          // memo: 下方向から表示された事をboundingClientRect.yを利用して判定する
          if (
            entry.isIntersecting &&
            entry.boundingClientRect.y <= boundingClientRectY
          ) {
            setIsBackToTopDisplay(true)
          } else if (
            entry.isIntersecting &&
            entry.boundingClientRect.y >= boundingClientRectY
          ) {
            setIsBackToTopDisplay(false)
          }

          boundingClientRectY = entry.boundingClientRect.y
        })
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.5,
      },
    )

    //memo ページ遷移時に発火しない場合がある為、ページ表示時にリセットをかねてstateを更新しています.
    setIsBackToTopDisplay(false)

    // memo: wrapperだとすでに表示されている場合が多いため、2つ目のh2見出しで表示させます。
    // h2が1つだった場合は、global-footerの表示タイミングで表示します
    const targetElements = document.querySelectorAll('h2')
    if (targetElements.length >= 2) {
      observer.observe(targetElements[1])
    } else {
      const targetElement = document.getElementById('footer')
      if (targetElement) {
        observer.observe(targetElement)
      }
    }
  }, [])

  return (
    <>
      <div className="header">
        <GlobalHeader pageType={prop.pageType} userType={prop.isHeaderType} />
        <Container fluid="lg">
          <GlobalNavigation isUserType={prop.isHeaderType} />
        </Container>
      </div>
      <LayoutScrollContext.Provider
        value={{ isJumpLinksSticky, registerJumpLinksSentinel }}
      >
        <div className={`body ${isIndex ? 'is-index' : ''}`}>
          {isIndex ? (
            <IndexComponent>{prop.children}</IndexComponent>
          ) : isGuide ? (
            <GuideComponentType breadcrumb={prop.breadcrumb}>
              {prop.children}
            </GuideComponentType>
          ) : isClientNoSideBar ? (
            <ClientNoSideBarComponent breadcrumb={prop.breadcrumb}>
              {prop.children}
            </ClientNoSideBarComponent>
          ) : (
            <BaseComponent
              breadcrumb={prop.breadcrumb}
              isSideBarType={prop.isSideBarType}
            >
              {prop.children}
            </BaseComponent>
          )}
        </div>
      </LayoutScrollContext.Provider>
      <div className="footer" id="footer">
        <React.Suspense fallback="">
          <GlobalFooter userType={prop.isFooterType} />
        </React.Suspense>
      </div>
      <ScrollToPageTopBtn isDisplay={isDisplayBackToTopButton} />
      {!prop.hideChatbot && (
        <React.Suspense fallback="">
          <Chatbot />
        </React.Suspense>
      )}
    </>
  )
}

export default Layout
